/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/components/ToolPanel/Metadata
 * @desc Add metadata fields to the Smart Table
 */

import { EntityDetailTable } from 'labstep-web/components/Entity/Detail/Table';
import { getRows } from 'labstep-web/components/Metadata/Detail/Table/Settings/utils';
import { MetadataShowName } from 'labstep-web/components/Metadata/Show';
import { ICONS } from 'labstep-web/constants/icons';
import { useGridSmartTableProtocolConditionServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { Header } from 'labstep-web/core/Header';
import { Icon } from 'labstep-web/core/Icon';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { getMetadataColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/metadata';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import { Metadata } from 'labstep-web/models/metadata';
import type { EntityDataGridToolPanelMetadataAddFieldProps } from './types';

export const EntityDataGridToolPanelMetadataAddField: React.FC<
  EntityDataGridToolPanelMetadataAddFieldProps
> = ({ activeVariable, experimentOrProtocol, fullscreen }) => {
  const { activeGroup } = useActiveGroup();

  const toolPanelContext = useToolPanelContext();

  const smartTableService =
    useGridSmartTableProtocolConditionServiceFromContext();

  return (
    <ReadOnMountHOC
      type="entities"
      entityName={Metadata.entityName}
      params={{
        get_single: 1,
        id: activeVariable.guid,
      }}
      loading={{ loader: 'placeholder' }}
      children={({ entity: metadata }): React.ReactElement => {
        const onSuccess = (response: any) => {
          if (
            (response.response.entities?.experiment ||
              response.response.entities?.protocol) &&
            smartTableService
          ) {
            const colDefs = [
              getMetadataColDef(
                experimentOrProtocol,
                metadata.parent_entity_class,
                metadata.type,
                metadata.label,
              ),
            ];

            smartTableService.updateColDef(colDefs);
            smartTableService.agGrid?.api.refreshCells({
              force: true,
              suppressFlash: true,
            });
            smartTableService.agGrid?.api.refreshHeader();
          }
        };

        return (
          <Flex column>
            <Flex vAlignContent="center" spacing="gap" shrink={0}>
              {fullscreen && (
                <ActionComponent
                  type="icon"
                  icon="arrow left"
                  text="Go Back"
                  onClick={() =>
                    toolPanelContext?.setToolPanelState(
                      ToolPanelState.add_column,
                    )
                  }
                />
              )}
              <Flex vAlignContent="center" spacing="gap">
                <Icon
                  name={
                    ICONS.metadata.type[
                      metadata.type as keyof typeof ICONS.metadata.type
                    ]
                  }
                />
                <Header size="tiny" noMargin>
                  <MetadataShowName id={metadata.id} />
                </Header>
              </Flex>
            </Flex>
            <EntityDetailTable
              dataCy="components/Metadata/Detail/Settings/Table"
              columns={[metadata]}
              rows={getRows(
                experimentOrProtocol,
                metadata,
                activeGroup!.id,
                onSuccess,
              )}
            />
          </Flex>
        );
      }}
    />
  );
};
